import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { SessionStore } from '@/app/core/state/session-store';
import { UserInfo } from '@/app/core/state/session.claims';
import { UserSettingsKeys } from '@/app/core/state/user-settings-keys';
import { UserSettingService } from '@/app/pages/dashboard/data-access/user-setting.service';
import {
  DashboardDefinition,
  DashboardKey,
  ResolvedDashboardContext,
} from '@/app/pages/dashboard/models/dashboard.models';
import {
  DASHBOARD_REGISTRY,
  getDashboardByKey,
  isValidDashboardKey,
} from '@/app/pages/dashboard/registry/dashboard-registry';

@Injectable({ providedIn: 'root' })
export class DashboardPersonaService {
  private readonly session = inject(SessionStore);
  private readonly userSettings = inject(UserSettingService);

  getCurrentUserInfo(): Promise<UserInfo> {
    const userInfo = this.session.userInfo();
    if (userInfo) {
      return Promise.resolve(userInfo);
    }

    return firstValueFrom(this.session.userInfo$.pipe(take(1)));
  }
  async getDefaultDashboardKey(userInfo: UserInfo): Promise<DashboardKey> {
    const savedKey = this.userSettings.get(UserSettingsKeys.LAST_DASHBOARD);
    if (
      savedKey &&
      isValidDashboardKey(savedKey) &&
      (await this.canAccessDashboard(userInfo, savedKey))
    ) {
      return savedKey;
    }
    return userInfo.IsASM ? 'operations' : 'customer';
  }
  rememberDashboard(key: DashboardKey): void {
    this.userSettings.set(UserSettingsKeys.LAST_DASHBOARD, key);
  }
  async getAllowedDashboards(
    userInfo: UserInfo
  ): Promise<DashboardDefinition[]> {
    if (!userInfo.IsASM) {
      return DASHBOARD_REGISTRY.filter(
        (dashboard) => dashboard.key === 'customer'
      );
    }
    return DASHBOARD_REGISTRY.filter(
      (dashboard) => !dashboard.asmOnly || userInfo.IsASM
    );
  }
  async canAccessDashboard(
    userInfo: UserInfo,
    key: DashboardKey
  ): Promise<boolean> {
    const allowedDashboards = await this.getAllowedDashboards(userInfo);
    return allowedDashboards.some((dashboard) => dashboard.key === key);
  }
  async resolveContext(
    key: DashboardKey
  ): Promise<ResolvedDashboardContext | null> {
    const userInfo = await this.getCurrentUserInfo();
    const definition = getDashboardByKey(key);
    if (!definition || !(await this.canAccessDashboard(userInfo, key))) {
      return null;
    }
    return {
      key,
      definition,
      allowedDashboards: await this.getAllowedDashboards(userInfo),
    };
  }
}
